import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import iconTitle from '../../assets/img/icon-square.svg';
import useRequireAuth from '../../hooks/useRequireAuth';

const AgencySolicitorsTab = ({ agencyId, solicitors, onStatusChange, onReturn, onAddSolicitor }) => {
  const navigate = useNavigate();
  const [filteredSolicitors, setFilteredSolicitors] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('すべて');
  const [searchField, setSearchField] = useState('担当者名漢字');

  const userInfo = useRequireAuth();

  // 初期化時に全データを設定
  useEffect(() => {
    setFilteredSolicitors(solicitors);
  }, [solicitors]);

  // 絞り込み処理を実行する関数 - useCallbackでメモ化
  const applyFilters = useCallback(() => {
    let results = [...solicitors]; // 全データからスタート
    
    // ステータスフィルター
    if (statusFilter === '利用中') {
      results = results.filter(solicitor => solicitor.is_active);
    } else if (statusFilter === '停止中') {
      results = results.filter(solicitor => !solicitor.is_active);
    }
    
    // 検索クエリフィルター
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      
      if (searchField === '担当者名漢字') {
        results = results.filter(solicitor => 
          solicitor.solicitor_name.toLowerCase().includes(query)
        );
      } else if (searchField === 'アカウント名（メールアドレス）') {
        results = results.filter(solicitor => 
          solicitor.accountname.toLowerCase().includes(query)
        );
      } 
    }
    
    setFilteredSolicitors(results);
  }, [searchQuery, searchField, statusFilter, solicitors]); // 依存する値を指定

  // 状態フィルターの変更ハンドラ
  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
  };
  
  // 検索ボタンのハンドラー
  const handleSearch = (e) => {
    e.preventDefault();
    applyFilters();
  };
  
  // 検索フィールド変更ハンドラー
  const handleSearchFieldChange = (e) => {
    setSearchField(e.target.value);
  };

  // 検索クエリの変更を監視して自動的に絞り込みを適用
  useEffect(() => {
    applyFilters();
  }, [applyFilters]); // applyFiltersのみを依存配列に追加

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return format(parseISO(dateString), 'yyyy/MM/dd');
  };

  // 編集ボタンがクリックされたときの処理
  const onEdit = (solicitorId) => {
    // ローカルストレージからagencyIdを取得
    const storedAgencyId = JSON.parse(localStorage.getItem('userInfo') || '{}').agency_id;
    const storedUserType = localStorage.getItem('userType');
    
    // agencyIdの有無で遷移先を分岐
    if (storedAgencyId) {
      navigate(`/agency/edit/${agencyId}?solicitorId=${solicitorId}`);
    } else {
      if (storedUserType === 'admin') {
        navigate(`/admin/agency/edit/${agencyId}?solicitorId=${solicitorId}`);
      } else if (storedUserType === 'agency') {
        navigate(`/agency/edit/${agencyId}?solicitorId=${solicitorId}`);
      }
    }
  }

  return (
    <div className="solicitor-container c-max-large">
      <div className="page-header">
        <div className="title-section">
          <div className="icon-square">
            <img src={iconTitle} alt="タイトルアイコン" />
          </div>
          <h2>募集人検索</h2>
        </div>
      </div>
      <div className="status-filters">
        <span className="status-label">状態</span>
        <div className="status-buttons">
          <button 
            className={`status-button ${statusFilter === 'すべて' ? 'active' : ''}`}
            onClick={() => handleStatusFilterChange('すべて')}
          >
            すべて
          </button>
          <button 
            className={`status-button ${statusFilter === '利用中' ? 'active' : ''}`}
            onClick={() => handleStatusFilterChange('利用中')}
          >
            利用中
          </button>
          <button 
            className={`status-button ${statusFilter === '停止中' ? 'active' : ''}`}
            onClick={() => handleStatusFilterChange('停止中')}
          >
            停止中
          </button>
        </div>
      </div>
      <div className="solicitor-header">
        <div className='c-flex-wrapper c-flex-wrapper--gap-3 c-flex-wrapper--center'>
          <div className="select-box ver04 c-max-small">
            <select 
              className="solicitor-select"
              value={searchField}
              onChange={handleSearchFieldChange}
            >
              <option value="担当者名漢字">担当者名漢字</option>
              <option value="アカウント名（メールアドレス）">アカウント名（メールアドレス）</option>
            </select>
          </div>
          <div className="search-container-solicitor long">
            <input 
              type="text" 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="例:担当者名漢字、メールアドレス"
              className="search-input"
            />
            <button className="search-button-solicitor" onClick={handleSearch}>
              <svg width="17" height="18" viewBox="0 0 17 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.98828 9.84033L10.0072 8.0235L16.0914 14.7844C16.5931 15.3419 16.5478 16.2005 15.9903 16.7022L15.9903 16.7022C15.4328 17.2039 14.5742 17.1587 14.0725 16.6012L7.98828 9.84033Z" fill="#3DA5E8"/>
                <path d="M14.0468 7.11545C14.0468 10.9643 10.9267 14.0844 7.07785 14.0844C3.229 14.0844 0.108887 10.9643 0.108887 7.11545C0.108887 3.2666 3.229 0.146484 7.07785 0.146484C10.9267 0.146484 14.0468 3.2666 14.0468 7.11545Z" fill="white"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M7.07785 11.0844C9.26985 11.0844 11.0468 9.30745 11.0468 7.11545C11.0468 4.92345 9.26985 3.14648 7.07785 3.14648C4.88585 3.14648 3.10889 4.92345 3.10889 7.11545C3.10889 9.30745 4.88585 11.0844 7.07785 11.0844ZM7.07785 14.0844C10.9267 14.0844 14.0468 10.9643 14.0468 7.11545C14.0468 3.2666 10.9267 0.146484 7.07785 0.146484C3.229 0.146484 0.108887 3.2666 0.108887 7.11545C0.108887 10.9643 3.229 14.0844 7.07785 14.0844Z" fill="#0073BC"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div className="page-header">
        <div className="title-section">
          <div className="icon-square">
            <img src={iconTitle} alt="タイトルアイコン" />
          </div>
          <h2 className='c-flex-wrapper c-flex-wrapper--search-result'>
            <span className='c-flex-wrapper--search-left'>検索結果</span>
            <strong className='c-flex-wrapper--search-right'>
              <span className='item-left'>{filteredSolicitors.length}</span> / <span className='item-right'>{solicitors.length}</span> 件
              </strong>
            </h2>
        </div>
      </div>
      <div className="solicitor-table-container">
        <table className="solicitor-table">
          <thead>
            <tr>
              <th>担当者名漢字</th>
              <th>アカウント名（メールアドレス）</th>
              {(!userInfo.is_agent || userInfo.is_admin) && (
                <>
                  <th>利用開始日</th>
                  <th>利用終了日</th>
                </>
              )}
              {/* adminユーザのみ仮パスワード列を表示 */}
              {userInfo.is_admin && (
                <th>仮パスワード</th>
              )}
              <th>&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {filteredSolicitors.length > 0 ? (
              filteredSolicitors.map(solicitor => (
                <tr 
                  key={solicitor.id}
                  style={{ 
                    backgroundColor: !solicitor.is_active ? '#6a686870' : 'inherit'
                  }}
                >
                  <td>{solicitor.solicitor_name}</td>
                  <td>{solicitor.accountname}</td>
                  {(!userInfo.is_agent || userInfo.is_admin) && (
                    <>
                      <td>{formatDate(solicitor.start_date)}</td>
                      <td>{formatDate(solicitor.end_date)}</td>
                    </>
                  )}
                  {/* adminユーザのみ仮パスワード表示 */}
                  {userInfo.is_admin && (
                    <td>{solicitor.temp_password || ''}</td>
                  )}
                  <td className='c-btn-wrapper'>
                    <div className="c-checkbox-inner">
                      <button 
                        className="btn-confirm-mini"
                        onClick={() => onEdit(solicitor.id)}
                      >
                        編集
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" style={{textAlign: 'center'}}>該当する募集人はいません</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="action-buttons c-max-mini c-m-center">
        <button 
          type="button" 
          className="button button-secondary button-medium" 
          onClick={onReturn}
        >
          戻る
        </button>
        <button 
          className="btn btn-primary button-medium"
          onClick={onAddSolicitor}
        >
          募集人追加
        </button>
      </div>
    </div>
  );
};

export default AgencySolicitorsTab;